export const FIELDS = ["name", "era", "year"] as const;
export type Field = (typeof FIELDS)[number];

export const GRADES = ["correct", "close", "incorrect", "skip"] as const;
export type Grade = (typeof GRADES)[number];

export type GameScope =
  | { kind: "all" }
  | { kind: "eras"; eraIds: string[] }
  | { kind: "custom"; setIds: string[] };

export type SessionLength = 5 | 10 | 20 | "endless";
export type Appearance = "system" | "light" | "dark";

export interface SetRecord {
  id: string;
  name: string;
  era: string;
  eraId: string;
  year: number;
  releaseDate: string | null;
  symbol: string;
  scale?: number;
}

export interface GameSettings {
  fields: Field[];
  clues: Field[];
  scope: GameScope;
  sessionLength: SessionLength;
  appearance: Appearance;
}

export interface FamiliarityRecord {
  name: number;
  era: number;
  year: number;
  seen: number;
  lastSeen: string | null;
}

export interface HistoryEntry {
  id: string;
  setId: string;
  asked: Field[];
  clues: Field[];
  answers: Partial<Record<Field, string | number>>;
  result: Partial<Record<Field, Grade>>;
  points: number;
  familiarityAfter: Partial<Record<Field, number>>;
  at: string;
}

export interface DailySnapshot {
  date: string;
  overall: number;
}

export interface PersistedStateV1 {
  version: 1;
  settings: GameSettings;
  familiarity: Record<string, FamiliarityRecord>;
  history: HistoryEntry[];
  totals: {
    score: number;
    streak: number;
  };
  dailySnapshots: DailySnapshot[];
}

export type AnswerValue = string | number;

export interface RoundAnswers {
  name?: string;
  era?: string;
  year?: string;
}

export interface RoundFeedback {
  set: SetRecord;
  answers: RoundAnswers;
  result: Record<Field, Grade>;
  hinted: Field[];
  points: number;
  perfect: boolean;
  familiarityBefore: Partial<Record<Field, number>>;
  familiarityAfter: Partial<Record<Field, number>>;
  at: string;
}

export interface SessionStats {
  answered: number;
  points: number;
  perfect: number;
  reviewedIds: string[];
  fieldStats: Record<Field, { correct: number; answered: number }>;
}

export type AppPhase = "asking" | "feedback" | "summary";

export interface SessionRuntime {
  phase: AppPhase;
  currentSet: SetRecord | null;
  recentSetIds: string[];
  feedback: RoundFeedback | null;
  stats: SessionStats;
  answers: RoundAnswers;
  skipped: Set<Field>;
  hinted: Set<Field>;
  validation: Partial<Record<Field, string>>;
  eraSelection: string | null;
}
